/**
 * Class strings for the viewport-sized app shell, kept dependency-free.
 *
 * Three surfaces draw the shell: the app layout (`AppSidebarLayout`), the
 * top-bar-only shell (`AppTopBarShell`), and the Sentry boundary's fallback
 * (`AppErrorBoundaryFallback`), which must stay renderable after the router
 * has thrown and so cannot import either component. All three import these
 * strings, and the keyboard browser test
 * renders the same strings, so the test exercises the markup users get rather
 * than a hand-copied approximation of it.
 *
 * The geometry itself (`--forge-visual-viewport-height`, `--forge-content-height`)
 * lives in `index.css` under "Global app shell metrics".
 */

/**
 * Nothing inside the shell may claim a viewport height of its own (`min-h-svh`,
 * `h-screen`, `100vh`). The shell is already the viewport, and under a software
 * keyboard it is shorter than `svh`: a child pinned to `svh` used to keep the
 * content column at full height, which pushed the composer under the keyboard
 * while the shell around it had correctly shrunk. Children size from the shell
 * with `flex-1 min-h-0`, and the routed area from `--forge-content-height`.
 */

/** The shell itself: one viewport tall, sidebar and content column side by side. */
export const APP_SHELL_ROW_CLASS = "forge-app-shell flex w-full min-w-0 flex-row";

/** The shell when there is no sidebar: bar on top, content below. */
export const APP_SHELL_COLUMN_LAYOUT_CLASS = "forge-app-shell flex w-full min-w-0 flex-col";

/** The content column beside the sidebar: the bar, then the routed content. */
export const APP_SHELL_CONTENT_COLUMN_CLASS = "flex min-h-0 min-w-0 flex-1 flex-col";

/** The routed content area under the bar, sized from `--forge-content-height`. */
export const APP_SHELL_MAIN_CLASS =
  "relative flex h-[var(--forge-content-height)] min-h-0 min-w-0 flex-1 overflow-hidden";
